import { MobileOfferCatalogEntry, MobileOfferPreview, MobileOfferProvider, MobileOfferProvisioning } from '@numeo/types'
import { useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'
import { OfferButton, OfferNotice, OfferSection } from './ui'
import { OfferPrices } from './OfferPrices'
import { StoreStatusBadge } from './StoreStatusBadge'
import { storeLabels } from './offerLabels'
import { AppleCodeBatch } from './AppleCodeBatch'

export function StoreOfferPanel({
    offer,
    provider,
    onChanged,
}: {
    offer: MobileOfferCatalogEntry
    provider: MobileOfferProvider
    onChanged: () => Promise<void>
}) {
    const [preview, setPreview] = useState<MobileOfferPreview | null>(null)
    const [operation, setOperation] = useState<MobileOfferProvisioning | null>(null)
    const [confirmed, setConfirmed] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const locked = useRef(false)
    const store = offer.stores[provider]
    useEffect(() => {
        let active = true
        mobileOffersApi
            .status(offer.id, provider)
            .then((result) => {
                if (active) setOperation(result)
            })
            .catch(() => {
                if (active) setError('Creation status could not be loaded. Refresh before continuing.')
            })
        return () => {
            active = false
        }
    }, [offer.id, provider])
    const run = async (action: () => Promise<void>) => {
        if (locked.current) return
        locked.current = true
        setBusy(true)
        setError('')
        setMessage('')
        try {
            await action()
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            locked.current = false
            setBusy(false)
        }
    }
    const refresh = async () => {
        setOperation(await mobileOffersApi.status(offer.id, provider))
        await onChanged()
    }
    const prices = store.status === 'ready' ? store.prices : preview?.prices
    return (
        <OfferSection title={storeLabels[provider]} action={<StoreStatusBadge store={store} />}>
            <OfferNotice>
                {provider === 'google'
                    ? 'An in-app subscription offer. Customers receive it through your app, not a shareable promo code.'
                    : 'A batch of one-time codes is generated as soon as the offer is created in the App Store (Apple can take up to an hour to activate it); top it up here.'}
            </OfferNotice>
            {store.status === 'ready' && (
                <p className="text-xs text-slate-500 dark:text-slate-400">Last verified {new Date(store.verifiedAt).toLocaleString()}</p>
            )}
            {operation && operation.status !== 'ready' && (
                <OfferNotice>
                    Saved creation status: {operation.status.replace('_', ' ')}.{' '}
                    {operation.status === 'needs_review' && 'Inspect the backend diagnostic before retrying. '}
                    Refresh reloads saved status only; it does not retry, create, or reconcile the store offer.
                </OfferNotice>
            )}
            {prices && <OfferPrices prices={prices} periods={offer.terms.discountedPeriods} />}
            {store.status === 'ready' && preview && (
                <details className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                    <summary className="cursor-pointer text-sm font-medium">Current price preview — not the configured offer</summary>
                    <OfferNotice>This is a new quote from current store prices. The configured offer above has not been changed.</OfferNotice>
                    <OfferPrices prices={preview.prices} periods={offer.terms.discountedPeriods} />
                </details>
            )}
            <div className="flex flex-wrap gap-2">
                <OfferButton
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                        void run(async () => {
                            setConfirmed(false)
                            setPreview(null)
                            setPreview(await mobileOffersApi.preview(offer.id, provider))
                        })
                    }
                >
                    {busy ? 'Working…' : 'Preview store prices'}
                </OfferButton>
                <OfferButton
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                        void run(async () => {
                            await refresh()
                            setMessage('Latest saved creation status loaded.')
                        })
                    }
                >
                    Refresh creation status
                </OfferButton>
            </div>
            {preview && store.status !== 'ready' && (
                <div className="space-y-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
                    <label className="flex items-start gap-2 text-sm">
                        <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 accent-blue-600"
                            checked={confirmed}
                            disabled={busy}
                            onChange={(event) => setConfirmed(event.target.checked)}
                        />
                        I approve these exact prices, countries and {offer.terms.discountedPeriods} monthly payments in {storeLabels[provider]}. This changes
                        live store configuration even for test purchases.
                    </label>
                    <OfferButton
                        disabled={busy || !confirmed}
                        onClick={() =>
                            void run(async () => {
                                setConfirmed(false)
                                try {
                                    setOperation(await mobileOffersApi.provision(preview))
                                } finally {
                                    await refresh()
                                }
                            })
                        }
                    >
                        Create / reconcile in {storeLabels[provider]}
                    </OfferButton>
                </div>
            )}
            {store.status === 'ready' && (
                <details className="rounded-lg border border-slate-200 p-3 text-xs dark:border-slate-700">
                    <summary className="cursor-pointer font-medium focus-visible:ring-2 focus-visible:ring-blue-600">Technical details</summary>
                    <dl className="mt-3 space-y-2 break-all text-slate-500 dark:text-slate-400">
                        <div>
                            <dt>Store offer ID</dt>
                            <dd>{store.reference.offerId}</dd>
                        </div>
                        <div>
                            <dt>Product / base plan</dt>
                            <dd>
                                {store.reference.productId} {store.reference.basePlanId}
                            </dd>
                        </div>
                        <div>
                            <dt>Resource</dt>
                            <dd>{store.reference.resourceId || '—'}</dd>
                        </div>
                    </dl>
                </details>
            )}
            {provider === 'apple' && store.status === 'ready' && <AppleCodeBatch offer={offer} />}
            {message && <OfferNotice>{message}</OfferNotice>}
            {error && <OfferNotice error>{error}</OfferNotice>}
        </OfferSection>
    )
}
